/**
 * The field style system, such as it is.
 *
 * ADR-0001 §3.1 treats the field UI as a correctness requirement rather than a matter of
 * taste, and these values are that requirement written down:
 *
 *  - **48px minimum touch targets.** A gloved thumb on a ladder does not hit a 32px button,
 *    and a missed tap during capture is a photo that does not exist.
 *  - **High contrast, dark ground.** Phones outdoors are read in direct sun at maximum
 *    brightness. A light theme washes out; white-on-near-black survives.
 *  - **Large type, no thin weights.** Read at arm's length, wet, in motion.
 *  - **Status colour is never the only signal.** Every coloured chip carries words too;
 *    roofing crews skew male and roughly one in twelve has a colour vision deficiency.
 *
 * Inline style objects rather than a CSS file. The app is small enough that the indirection
 * would cost more than it saves, and keeping the values here means the constraints above
 * are visible next to the numbers that implement them.
 */

import type { CSSProperties } from 'react';

import type { Tone } from './status-text.js';

export const colors = {
  ground: '#0b0e13',
  raised: '#151a22',
  line: '#232b37',
  text: '#f5f7fa',
  dim: '#aeb8c7',
  faint: '#7d8899',
  accent: '#3d8bfd',
} as const;

/** Tone → chip colours. Paired with words, never used alone. */
export const toneStyles: Readonly<Record<Tone, CSSProperties>> = {
  good: { background: '#10331f', color: '#7ee2a8', border: `2px solid #1f7a47` },
  info: { background: '#122436', color: '#7cc0ff', border: `2px solid #245d91` },
  warn: { background: '#3a2a0c', color: '#ffc95c', border: `2px solid #a97612` },
  bad: { background: '#3a1414', color: '#ff9c9c', border: `2px solid #a13232` },
};

export const styles = {
  app: {
    minHeight: '100dvh',
    margin: 0,
    background: colors.ground,
    color: colors.text,
    fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.9rem 1rem',
    borderBottom: `1px solid ${colors.line}`,
    position: 'sticky',
    top: 0,
    background: colors.ground,
    zIndex: 2,
  },
  title: { margin: 0, fontSize: '1.35rem', letterSpacing: '-0.01em' },
  body: {
    flex: 1,
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    // Room for a thumb resting at the bottom of a phone, and for iOS home indicators.
    paddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))',
  },
  banner: {
    minHeight: '3rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '0.2rem',
    padding: '0.6rem 1rem',
    borderRadius: '0.6rem',
    fontSize: '1.05rem',
    fontWeight: 650,
  },
  bannerDetail: { fontSize: '0.85rem', fontWeight: 500, opacity: 0.9 },
  button: {
    minHeight: '3rem',
    padding: '0 1.25rem',
    borderRadius: '0.6rem',
    border: `2px solid ${colors.line}`,
    background: colors.raised,
    color: colors.text,
    fontSize: '1.05rem',
    fontWeight: 650,
    cursor: 'pointer',
  },
  buttonPrimary: {
    background: colors.accent,
    borderColor: colors.accent,
    color: '#04121f',
  },
  card: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '0.9rem 1rem',
    borderRadius: '0.7rem',
    border: `1px solid ${colors.line}`,
    background: colors.raised,
    color: colors.text,
    cursor: 'pointer',
  },
  cardTitle: { margin: 0, fontSize: '1.1rem', fontWeight: 650 },
  cardMeta: { margin: '0.25rem 0 0', fontSize: '0.9rem', color: colors.dim },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.15rem 0.5rem',
    borderRadius: '0.4rem',
    fontSize: '0.78rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
    fontSize: '0.95rem',
    color: colors.dim,
  },
  input: {
    minHeight: '3rem',
    padding: '0 0.85rem',
    borderRadius: '0.5rem',
    border: `2px solid ${colors.line}`,
    background: colors.ground,
    color: colors.text,
    // 16px minimum: anything smaller makes iOS Safari zoom the viewport on focus, which on
    // a one-handed capture flow is genuinely disorienting.
    fontSize: '1rem',
  },
  slot: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem',
    padding: '0.9rem',
    borderRadius: '0.7rem',
    border: `1px solid ${colors.line}`,
    background: colors.raised,
  },
  slotWhy: { margin: 0, fontSize: '0.85rem', color: colors.faint },
  thumbRow: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem' },
  thumb: {
    width: '5.5rem',
    height: '5.5rem',
    objectFit: 'cover',
    borderRadius: '0.4rem',
    border: `1px solid ${colors.line}`,
    background: colors.ground,
    display: 'block',
  },
  footer: {
    padding: '1rem',
    borderTop: `1px solid ${colors.line}`,
    fontSize: '0.75rem',
    color: colors.faint,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    wordBreak: 'break-all',
  },
} as const satisfies Record<string, CSSProperties>;
